import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Users, MapPin, Calendar as CalendarIcon, Clock, MessageSquare, LogOut, X, Share2, Trophy } from 'lucide-react';
import LevelBadge, { ExpProgressBar } from '@/components/LevelBadge';

const Events = ({ user, onBack }) => {
  const [events, setEvents] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventZipCode, setEventZipCode] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [eventLimit, setEventLimit] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const commentsEndRef = useRef(null);
  
  // Swipe detection refs
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const lastSwipeTime = useRef(0);

  useEffect(() => {
    const loadEvents = async () => {
      await fetchEvents();
    };
    loadEvents();
  }, []);

  // Auto-select closest upcoming event
  useEffect(() => {
    if (events.length > 0 && !selectedEvent) {
      // Find the closest upcoming event
      const now = new Date();
      const upcomingEvents = events.filter(event => {
        const eventDateTime = new Date(`${event.date}T${event.time}`);
        return eventDateTime >= now;
      }).sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time}`);
        const dateB = new Date(`${b.date}T${b.time}`);
        return dateA - dateB;
      });

      // If there are upcoming events, select the closest one
      if (upcomingEvents.length > 0) {
        setSelectedEvent(upcomingEvents[0]);
      } else if (events.length > 0) {
        // If no upcoming events, select the most recent past event
        const sortedEvents = [...events].sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.time}`);
          const dateB = new Date(`${b.date}T${b.time}`);
          return dateB - dateA; // Most recent first
        });
        setSelectedEvent(sortedEvents[0]);
      }
    }
  }, [events]);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventComments(selectedEvent.id);
    }
  }, [selectedEvent]);

  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  // Swipe detection for back navigation
  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;
      
      // Check if horizontal swipe is dominant
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        const minSwipeDistance = 48; // ~0.5 inch at 96 DPI
        const now = Date.now();
        const cooldown = 1000; // 1 second
        
        if (Math.abs(deltaX) >= minSwipeDistance && now - lastSwipeTime.current >= cooldown) {
          lastSwipeTime.current = now;
          
          // Both events list and event details pages go back to dashboard
          onBack();
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onBack]);

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (time) => {
    // Convert HH:MM to 12-hour format with AM/PM
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleShareEvent = async (event) => {
    const shareUrl = `${window.location.origin}/share/event/${event.id}`;
    const shareData = {
      title: event.title,
      text: `${event.description}\n\nJoin ${event.creator_username} at ${event.location}`,
      url: shareUrl
    };

    try {
      // Try native Web Share API first (mobile)
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Event shared!');
      } else {
        // Fallback: Copy link to clipboard
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Event link copied to clipboard!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        toast.error('Failed to share event');
      }
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchEventComments = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${eventId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventTitle.trim()) {
      toast.error('Please enter an event title');
      return;
    }
    if (!eventDescription.trim()) {
      toast.error('Please enter an event description');
      return;
    }
    if (!eventLocation.trim()) {
      toast.error('Please enter an event location');
      return;
    }
    if (!eventDate) {
      toast.error('Please select an event date');
      return;
    }
    if (!eventTime) {
      toast.error('Please select a start time');
      return;
    }
    if (!eventEndTime) {
      toast.error('Please select an end time');
      return;
    }

    // Validate event date and duration
    const startTime = new Date(`${eventDate}T${eventTime}`);
    let endTime = new Date(`${eventDate}T${eventEndTime}`);
    const now = new Date();
    
    // Check if event is in the past
    if (startTime < now) {
      toast.error('Event start time cannot be in the past');
      return;
    }
    
    // Check if event is more than 1 year in the future
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    if (startTime > oneYearFromNow) {
      toast.error('Events cannot be scheduled more than 1 year in the future');
      return;
    }
    
    // If end time is before start time, it means next day
    if (endTime < startTime) {
      endTime.setDate(endTime.getDate() + 1);
    }
    
    const durationHours = (endTime - startTime) / (1000 * 60 * 60);
    
    if (durationHours <= 0) {
      toast.error('End time must be after start time');
      return;
    }
    
    if (durationHours > 20) {
      toast.error('Event duration cannot exceed 20 hours');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/events/create`,
        {
          title: eventTitle,
          description: eventDescription,
          location: eventLocation,
          zip_code: eventZipCode || null,
          date: eventDate,
          time: eventTime,
          end_time: eventEndTime,
          attendee_limit: eventLimit ? parseInt(eventLimit) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Event created!');
      setEventTitle('');
      setEventDescription('');
      setEventLocation('');
      setEventZipCode('');
      setEventDate('');
      setEventTime('');
      setEventEndTime('');
      setEventLimit('');
      setShowCreateModal(false);
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleSignUp = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/events/${eventId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Signed up for event!');
      fetchEvents();
      if (selectedEvent && selectedEvent.id === eventId) {
        const updatedEvent = events.find(e => e.id === eventId);
        if (updatedEvent) {
          setSelectedEvent(updatedEvent);
        }
      }
    } catch (error) {
      console.error('Error signing up:', error);
      toast.error(error.response?.data?.detail || 'Failed to sign up');
    }
  };

  const handleLeaveEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/events/${eventId}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Left event');
      fetchEvents();
      if (selectedEvent && selectedEvent.id === eventId) {
        setSelectedEvent(null);
      }
    } catch (error) {
      console.error('Error leaving event:', error);
      toast.error('Failed to leave event');
    }
  };

  const handlePostComment = async () => {
    if (!commentInput.trim() || !selectedEvent) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/events/${selectedEvent.id}/comments`,
        { content: commentInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCommentInput('');
      setComments([...comments, response.data.comment]);
      toast.success('Comment posted!');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error(error.response?.data?.detail || 'Failed to post comment');
    }
  };

  const isUserSignedUp = (event) => {
    return event.attendees?.includes(user.id) || false;
  };

  const isEventFull = (event) => {
    if (!event.attendee_limit) return false;
    return (event.attendee_count || 0) >= event.attendee_limit;
  };

  if (selectedEvent) {
    const isSignedUp = isUserSignedUp(selectedEvent);
    const isCreator = selectedEvent.creator_id === user.id;
    
    return (
      <div 
        className="min-h-screen relative overflow-hidden flex flex-col"
        style={user.message_area_background ? {
          backgroundImage: `url(${user.message_area_background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        } : {
          background: 'linear-gradient(to bottom right, rgb(2, 6, 23), rgb(15, 23, 42), rgb(2, 6, 23))'
        }}
      >
        {/* Background effects - only show if no custom background */}
        {!user.message_area_background && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute w-96 h-96 bg-green-500/5 rounded-full blur-3xl top-20 left-20 animate-pulse-slow"></div>
            <div className="absolute w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl bottom-20 right-20 animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
          </div>
        )}

        <div className="relative z-10 max-w-4xl mx-auto w-full flex flex-col h-screen">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <Button
              onClick={() => setSelectedEvent(null)}
              variant="ghost"
              className="text-cyan-400 hover:text-cyan-300"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Events
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => handleShareEvent(selectedEvent)}
                variant="outline"
                className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              {!isCreator && (
                <>
                  {!isSignedUp ? (
                    <Button
                      onClick={() => handleSignUp(selectedEvent.id)}
                      disabled={isEventFull(selectedEvent)}
                      variant="outline"
                      className={`${
                        isEventFull(selectedEvent)
                          ? 'border-slate-500 text-slate-400 cursor-not-allowed'
                          : 'border-green-500 text-green-400 hover:bg-green-500/10'
                      }`}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      {isEventFull(selectedEvent) ? 'Full' : 'Sign Up'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleLeaveEvent(selectedEvent.id)}
                      variant="outline"
                      className="border-red-500 text-red-400 hover:bg-red-500/10"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Leave Event
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto pb-4 space-y-4">
            {/* Event Banner with Info */}
            <div 
              className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden backdrop-blur-sm relative"
              style={selectedEvent.creator_banner ? {
                backgroundImage: `url(${selectedEvent.creator_banner})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              } : {}}
            >
            {/* Overlay for text visibility */}
            {selectedEvent.creator_banner && (
              <div className="absolute inset-0 bg-black/60"></div>
            )}
            
            <div className="p-6 relative z-10">
              <h2 className={`text-3xl font-bold mb-2 ${selectedEvent.creator_banner ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                {selectedEvent.title}
              </h2>
              <p className={`mb-4 ${selectedEvent.creator_banner ? 'text-white/90 drop-shadow' : 'text-slate-300'}`}>
                {selectedEvent.description}
              </p>
              
              <div className={`grid grid-cols-2 gap-3 text-sm ${selectedEvent.creator_banner ? 'text-white/90' : 'text-slate-400'}`}>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{selectedEvent.location}{selectedEvent.zip_code ? `, ${selectedEvent.zip_code}` : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  <span>{new Date(selectedEvent.date).toLocaleDateString('en-US', { weekday: 'long' })} {new Date(selectedEvent.date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{formatTime(selectedEvent.time)} - {formatTime(selectedEvent.end_time)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span>
                    {selectedEvent.attendee_count || 0}
                    {selectedEvent.attendee_limit ? ` / ${selectedEvent.attendee_limit}` : ''} signed up
                  </span>
                </div>
              </div>
              
              <div className={`mt-4 text-sm ${selectedEvent.creator_banner ? 'text-white/80' : 'text-slate-500'}`}>
                Created by {selectedEvent.creator_username}
              </div>
            </div>
          </div>

          {/* Signed Up Players */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-4 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-cyan-400 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Signed Up Players ({selectedEvent.attendee_count || 0})
              </h3>
              <Input
                type="text"
                placeholder="Search..."
                value={playerSearchQuery}
                onChange={(e) => setPlayerSearchQuery(e.target.value)}
                className="w-32 h-8 px-2 bg-slate-900/50 border-slate-600 text-white text-base placeholder:text-slate-400"
              />
            </div>
            
            {selectedEvent.attendee_profiles && selectedEvent.attendee_profiles.length > 0 ? (
              (() => {
                const filteredPlayers = selectedEvent.attendee_profiles.filter(attendee => 
                  attendee.username.toLowerCase().includes(playerSearchQuery.toLowerCase())
                );
                
                return filteredPlayers.length > 0 ? (
                  <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {filteredPlayers.map((attendee) => (
                      <div
                        key={attendee.id}
                        className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 flex justify-between items-center relative overflow-hidden"
                        style={attendee.banner_image ? {
                          backgroundImage: `url(${attendee.banner_image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat'
                        } : {}}
                      >
                        {/* Overlay for better text visibility when banner is set */}
                        {attendee.banner_image && (
                          <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                        )}
                        
                        {/* Level Badge */}
                        <LevelBadge level={attendee.level || 1} small={true} verified={attendee.has_made_first_purchase} />
                        
                        <div className="flex items-center gap-3 relative z-10">
                          {/* Profile Picture */}
                          {attendee.profile_picture ? (
                            <img
                              src={attendee.profile_picture}
                              alt={attendee.username}
                              className="w-12 h-12 rounded-full object-cover border-2 pointer-events-none"
                              style={{ borderColor: attendee.circle_color || '#475569' }}
                            />
                          ) : (
                            <div 
                              className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-300 text-sm font-semibold pointer-events-none"
                              style={{ borderColor: attendee.circle_color || '#475569' }}
                            >
                              {attendee.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                          {/* Username */}
                          <span className={`font-semibold relative z-10 ${attendee.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                            {attendee.username}
                          </span>
                        </div>
                        {/* Battle Points - Right Side */}
                        <span className={`digital-font font-semibold relative z-10 ${attendee.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                          {attendee.battle_points || 0} BP
                        </span>
                        
                        {/* Trophy System - Top Right */}
                        <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                          <div className="flex items-center gap-0.5">
                            <Trophy className="w-3 h-3 text-yellow-500" />
                            <span className="text-[10px] font-bold text-white drop-shadow">{attendee.gold_trophies || 0}</span>
                          </div>
                          <div className="flex items-center gap-0.5">
                            <Trophy className="w-3 h-3 text-slate-300" />
                            <span className="text-[10px] font-bold text-white drop-shadow">{attendee.silver_trophies || 0}</span>
                          </div>
                          <div className="flex items-center gap-0.5">
                            <Trophy className="w-3 h-3 text-amber-600" />
                            <span className="text-[10px] font-bold text-white drop-shadow">{attendee.bronze_trophies || 0}</span>
                          </div>
                        </div>
                        
                        {/* EXP Progress Bar */}
                        <ExpProgressBar level={attendee.level || 1} exp={attendee.exp || 0} />
                      </div>
                    ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-slate-400 py-4">
                    No players found matching "{playerSearchQuery}"
                  </div>
                );
              })()
            ) : (
              <div className="text-center text-slate-400 py-4">
                No players signed up yet
              </div>
            )}
          </div>

            {/* Comments Section - Chat Style */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg backdrop-blur-sm overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-lg font-semibold text-cyan-400 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Comments ({comments.length})
                </h3>
              </div>
              
              {/* Comments List - Chat Bubbles */}
              <div className="p-4 space-y-3">
                {comments.length === 0 ? (
                  <div className="text-center text-slate-400 py-8">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No comments yet. {isSignedUp || isCreator ? 'Be the first to comment!' : 'Sign up to comment!'}</p>
                  </div>
                ) : (
                  comments.map((comment) => {
                    const isOwnComment = comment.user_id === user.id;
                    return (
                      <div
                        key={comment.id}
                        className={`flex gap-2 ${isOwnComment ? 'flex-row-reverse' : 'flex-row'}`}
                      >
                        {/* Profile Picture */}
                        <img
                          src={comment.user_profile_picture || '/default-avatar.png'}
                          alt={comment.username}
                          className="w-8 h-8 rounded-full object-cover border-2 flex-shrink-0"
                          style={{ borderColor: comment.user_circle_color || '#475569' }}
                        />
                        
                        {/* Message Bubble */}
                        <div className={`flex flex-col max-w-[70%] ${isOwnComment ? 'items-end' : 'items-start'}`}>
                          <div 
                            className="text-xs mb-1 px-2 py-0.5 rounded-lg relative overflow-hidden"
                            style={comment.user_banner ? {
                              backgroundImage: `url(${comment.user_banner})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            } : {}}
                          >
                            {comment.user_banner && (
                              <div className="absolute inset-0 bg-black/60"></div>
                            )}
                            <span className={`relative z-10 ${comment.user_banner ? 'text-white drop-shadow' : 'text-slate-400'}`}>
                              {comment.username}
                            </span>
                          </div>
                          <div
                            className={`rounded-2xl px-4 py-2 text-white ${
                              isOwnComment
                                ? 'rounded-tr-sm'
                                : 'rounded-tl-sm'
                            }`}
                            style={{
                              backgroundColor: isOwnComment 
                                ? (user.banner_color || '#3B82F6')
                                : (comment.user_circle_color || '#3B82F6')
                            }}
                          >
                            <p className="text-sm break-words">{comment.content}</p>
                          </div>
                          <div className="text-xs text-slate-500 mt-1 px-1">
                            {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={commentsEndRef} />
              </div>
            </div>
          </div>

          {/* Fixed Comment Input at Bottom - Only for signed up users */}
          {(isSignedUp || isCreator) && (
            <div className="flex-shrink-0 bg-slate-800/50 border-t border-slate-700 p-4 backdrop-blur-sm">
              <div className="flex gap-2 max-w-4xl mx-auto">
                <Input
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePostComment()}
                  placeholder="Write a comment..."
                  className="flex-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-400"
                />
                <Button
                  onClick={handlePostComment}
                  disabled={!commentInput.trim()}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Post
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-4 relative overflow-hidden"
      style={user.message_area_background ? {
        backgroundImage: `url(${user.message_area_background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      } : {
        background: 'linear-gradient(to bottom right, rgb(2, 6, 23), rgb(15, 23, 42), rgb(2, 6, 23))'
      }}
    >
      {/* Background effects - only show if no custom background */}
      {!user.message_area_background && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-96 h-96 bg-green-500/5 rounded-full blur-3xl top-20 left-20 animate-pulse-slow"></div>
          <div className="absolute w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl bottom-20 right-20 animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
      )}

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={onBack}
                variant="ghost"
                className="text-cyan-400 hover:text-cyan-300"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <h1 className="text-3xl font-bold text-cyan-400">Events</h1>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </div>
          
          {/* Search Bar */}
          <Input
            type="text"
            placeholder="Search events by title, location, or creator..."
            value={eventSearchQuery}
            onChange={(e) => setEventSearchQuery(e.target.value)}
            className="w-full bg-slate-900/50 border-slate-600 text-white text-base placeholder:text-slate-400"
          />
        </div>

        {/* Events List */}
        <div className="space-y-4">
          {events.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400 text-lg mb-4">No events yet</p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create the first event
              </Button>
            </div>
          ) : (() => {
            const filteredEvents = events.filter(event => 
              event.title.toLowerCase().includes(eventSearchQuery.toLowerCase()) ||
              event.description.toLowerCase().includes(eventSearchQuery.toLowerCase()) ||
              event.location.toLowerCase().includes(eventSearchQuery.toLowerCase()) ||
              event.creator_username.toLowerCase().includes(eventSearchQuery.toLowerCase())
            );
            
            return filteredEvents.length === 0 ? (
              <div className="text-center py-12">
                <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400 text-lg mb-4">No events found matching "{eventSearchQuery}"</p>
                <Button
                  onClick={() => setEventSearchQuery('')}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  Clear Search
                </Button>
              </div>
            ) : (
              filteredEvents.map((event) => {
              const isSignedUp = isUserSignedUp(event);
              const isFull = isEventFull(event);
              
              return (
                <div
                  key={event.id}
                  className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden backdrop-blur-sm hover:border-cyan-500/50 transition-colors relative cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                  style={event.creator_banner ? {
                    backgroundImage: `url(${event.creator_banner})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  } : {}}
                >
                  {event.creator_banner && (
                    <div className="absolute inset-0 bg-black/60"></div>
                  )}
                  
                  <div className="p-4 relative z-10">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className={`text-xl font-semibold mb-1 ${event.creator_banner ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                          {event.title}
                        </h3>
                        <p className={`text-sm mb-2 ${event.creator_banner ? 'text-white/90 drop-shadow' : 'text-slate-300'}`}>
                          {event.description}
                        </p>
                      </div>
                      {isSignedUp && (
                        <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-xs font-semibold border border-green-500/50">
                          Signed Up
                        </span>
                      )}
                      {!isSignedUp && isFull && (
                        <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-xs font-semibold border border-red-500/50">
                          Full
                        </span>
                      )}
                    </div>
                    
                    <div className={`grid grid-cols-2 gap-2 text-sm mb-3 ${event.creator_banner ? 'text-white/90' : 'text-slate-400'}`}>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location}{event.zip_code ? `, ${event.zip_code}` : ''}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4" />
                        <span>{new Date(event.date).toLocaleDateString('en-US', { weekday: 'long' })} {new Date(event.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{formatTime(event.time)} - {formatTime(event.end_time)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>
                          {event.attendee_count || 0}
                          {event.attendee_limit ? ` / ${event.attendee_limit}` : ''}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className={`text-xs ${event.creator_banner ? 'text-white/80' : 'text-slate-500'}`}>
                        Created by {event.creator_username}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isSignedUp) {
                              handleLeaveEvent(event.id);
                            } else {
                              handleSignUp(event.id);
                            }
                          }}
                          disabled={!isSignedUp && isFull}
                          size="sm"
                          className={isSignedUp 
                            ? "bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30"
                            : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                          }
                        >
                          {isSignedUp ? 'Leave' : (isFull ? 'Full' : 'Sign Up')}
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShareEvent(event);
                          }}
                          size="sm"
                          className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 hover:bg-cyan-500/30"
                        >
                          <Share2 className="w-3 h-3 mr-1" />
                          Share
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }));
          })()}
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-cyan-400">Create Event</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEventTitle('');
                  setEventDescription('');
                  setEventLocation('');
                  setEventZipCode('');
                  setEventDate('');
                  setEventTime('');
                  setEventEndTime('');
                  setEventLimit('');
                }}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Title *</label>
                <Input
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  placeholder="Event name"
                  className="bg-slate-900/50 border-slate-600 text-white"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Description *</label>
                <textarea
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                  placeholder="What's this event about?"
                  rows={3}
                  className="w-full bg-slate-900/50 border border-slate-600 rounded-lg p-3 text-white placeholder:text-slate-400"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Location *</label>
                <Input
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  placeholder="Where will this happen?"
                  className="bg-slate-900/50 border-slate-600 text-white"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-300 mb-2 block">ZIP Code</label>
                <Input
                  value={eventZipCode}
                  onChange={(e) => setEventZipCode(e.target.value)}
                  placeholder="Enter ZIP code (optional)"
                  className="bg-slate-900/50 border-slate-600 text-white"
                  maxLength={10}
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Date *</label>
                <Input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]}
                  className="bg-slate-900/50 border-slate-600 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Start Time *</label>
                  <Input
                    type="time"
                    value={eventTime}
                    onChange={(e) => setEventTime(e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">End Time *</label>
                  <Input
                    type="time"
                    value={eventEndTime}
                    onChange={(e) => setEventEndTime(e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Player Limit (optional)</label>
                <Input
                  type="number"
                  value={eventLimit}
                  onChange={(e) => setEventLimit(e.target.value)}
                  placeholder="Leave empty for unlimited"
                  min="1"
                  className="bg-slate-900/50 border-slate-600 text-white"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEventTitle('');
                    setEventDescription('');
                    setEventLocation('');
                    setEventZipCode('');
                    setEventDate('');
                    setEventTime('');
                    setEventEndTime('');
                    setEventLimit('');
                  }}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateEvent}
                  disabled={creating}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                >
                  {creating ? 'Creating...' : 'Create Event'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;